import dynamic from 'next/dynamic';
import { Component } from 'react';
import type { Subscription } from 'rxjs';

import { container } from '../../context/module';
import { ImpersonationService } from '../../services/impersonation.service';
import { LoginService } from '../../services/login.service';
import { UIContext } from '../../services/ui-context';
import {
    ImpersonationEndReason,
    ImpersonationRecord,
    onImpersonationChange,
    readImpersonation,
    takeImpersonationEndedNotice,
} from '../../utils/impersonation-storage';
import { stopThirdPartyAnalytics } from '../../utils/impersonation-analytics';

/**
 * Mounted once in `_app`. Renders NOTHING (and loads nothing) unless this tab views as a user: an `AUTH_IMP` record in
 * sessionStorage, or `/auth/user-details` saying the bearer is an impersonation token (contract §4). Only then is the
 * banner chunk fetched. Client-only (state starts empty, filled in componentDidMount), so SSR and hydration match.
 */
const ImpersonationBanner = dynamic(() => import('./impersonation-banner.component'), { ssr: false });

interface ServerView {
    sessionId: string;
    email: string;
    packageType: string | null;
    expiresAt: string | null;
    actorEmail: string | null;
}

interface ImpersonationGateState {
    record: ImpersonationRecord | null;
    server: ServerView | null;
}

const ENDED_TEXT: Partial<Record<ImpersonationEndReason, string>> = {
    expired: 'The 30-minute "view as user" session ran out. You are back in your admin account.',
    ended: 'The "view as user" session was ended. You are back in your admin account.',
    unauthorized: 'The "view as user" session is no longer valid. You are back in your admin account.',
    session_cleared: 'The "view as user" session ended. You are back in your admin account.',
};

export class ImpersonationGate extends Component<Record<string, never>, ImpersonationGateState> {
    state: ImpersonationGateState = { record: null, server: null };
    private offChange: (() => void) | null = null;
    private sub: Subscription | null = null;

    componentDidMount() {
        if (typeof window === 'undefined') return;
        const ended = takeImpersonationEndedNotice();
        if (ended && ENDED_TEXT[ended]) {
            try { container.get(UIContext).warnToast('Viewing as user ended', ENDED_TEXT[ended]!, false, false); } catch { /* best effort */ }
        }
        // M4: a session that starts (or is reported by the server) after GA/Clarity loaded on this page turns them off.
        this.offChange = onImpersonationChange((record) => {
            if (record) stopThirdPartyAnalytics();
            this.setState({ record });
        });
        const current = readImpersonation();
        if (current) stopThirdPartyAnalytics();
        this.setState({ record: current });
        this.sub = container.get(LoginService).onUserDetailsChanged.subscribe((d) => {
            const imp = d?.impersonation;
            const server: ServerView | null = imp?.sessionId
                ? {
                    sessionId: imp.sessionId,
                    email: d.email,
                    packageType: (d.packageType as string) ?? null,
                    expiresAt: imp.expiresAt ?? null,
                    actorEmail: imp.actorEmail ?? null,
                }
                : null;
            this.setState({ server });
            if (server) stopThirdPartyAnalytics();
            if (server && !readImpersonation()) {
                container.get(ImpersonationService).resumeFromServer(imp);
            }
        });
    }

    componentWillUnmount() {
        this.offChange?.();
        this.sub?.unsubscribe();
    }

    private onEnd = () => container.get(ImpersonationService).end('user');

    render() {
        const { record, server } = this.state;
        if (!record && !server) return null;
        return (
            <ImpersonationBanner
                email={record?.target.email || server?.email || ''}
                packageType={server ? server.packageType : record?.target.packageType}
                expiresAt={record?.expiresAt ?? server?.expiresAt ?? null}
                actorEmail={server?.actorEmail ?? null}
                onEnd={this.onEnd}
            />
        );
    }
}

export default ImpersonationGate;
